import React, { memo, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import _ from "lodash";

import { Button, EmptyState, IconButton } from "@toddle-edu/ds-web";
import { ChevronLeftOutlined, ArrowOutOutlined } from "@toddle-edu/ds-icons";
import {
  AllCompletedIllustration,
  Error404Illustration,
} from "@toddle-edu/ds-theme";

import { compose, graphql } from "lib/apolloClient";
import { useI18n } from "Hooks";

import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import { openLinkInNewTab } from "modules/Services";
import { getSelectedCurriculumId } from "modules/CommonSelectors";
import { getLtiLaunchData } from "./modules/LTIQueries";
import { getLTIName, LTI_SERVICE_DETAILS } from "./modules/Utils";

import UIModal from "UIComponents/UIModal";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import Loading from "UIComponents/Loading";
import classes from "./LTIComponent.scss";
import LTIIframe from "./LTIIframe";

import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";

const ShowEmptyStateComp = props => {
  const {
    t,
    studentName,
    serviceName,
    userType,
    illustration,
    emptyStateTitle,
    emptyStateSubTitle,
    openLinkInNewTab,
  } = props;

  const onRedirectionClicked = () => {
    const dashBoardRedirectionUrl = _.get(
      LTI_SERVICE_DETAILS[serviceName],
      "dashBoardRedirectionUrl",
      null
    );
    if (!_.isNil(dashBoardRedirectionUrl)) {
      openLinkInNewTab({ url: dashBoardRedirectionUrl });
    }
  };
  const serviceLabel = getLTIName(serviceName) || serviceName;

  const getNoPreviewContent = () => {
    switch (userType) {
      case STAFF: {
        return (
          <EmptyState
            illustration={AllCompletedIllustration}
            size="large"
            title={t("classRoom:family_successfully_submitted_your_work", {
              studentName,
            })}
            subtitle={t("classRoom:go_to_label_view_student_work", {
              label: serviceLabel,
              studentName,
            })}
            dsVersion="2.0"
            primaryButton={
              <Button
                dsVersion="2.0"
                variant="neutral"
                type="outlined"
                onClick={onRedirectionClicked}
                icon={<ArrowOutOutlined />}
              >
                {t("classRoom:go_to_label", {
                  label: serviceLabel,
                })}
              </Button>
            }
          />
        );
      }
      case STUDENT:
      case PARENT: {
        return (
          <div className={classes.noPreviewStudentContainer}>
            <EmptyState
              illustration={illustration}
              size="small"
              title={emptyStateTitle}
              subtitle={emptyStateSubTitle}
              dsVersion="2.0"
            />
          </div>
        );
      }
    }
  };

  return (
    <div className={classes.noPreviewContainer}>{getNoPreviewContent()}</div>
  );
};

let LTIComponentWrapper = memo(props => {
  const {
    launchObject,
    initialLaunchUrl,
    showModal = true,
    setLTIData,
    stopLinkCreation,
    attachmentContextId,
    userType,
    t,
    serviceName,
    studentName,
    showEmptyState = false,
    illustration,
    emptyStateTitle,
    emptyStateSubTitle,
    onFrameLoaded,
    openLinkInNewTab,
    isError,
    isLoading,
    handleLoading,
    clearMemoizedCache,
    isLTIMobileView,
  } = props;

  useEffect(() => {
    setLTIData({
      launchUrl: initialLaunchUrl,
      stopLinkCreation,
      attachmentContextId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    return () => {
      clearMemoizedCache?.();
    };
  }, [clearMemoizedCache]);

  if (isLoading) {
    return null;
  }

  if (isError) {
    return (
      <EmptyState
        dsVersion="2.0"
        illustration={Error404Illustration}
        title={t("common:something_went_wrong")}
        subtitle={t("common:please_try_again_or_contact_admin")}
      />
    );
  }

  return (
    <div className={classes.ltiContentContainer}>
      {showEmptyState ? (
        <ShowEmptyStateComp
          illustration={illustration}
          userType={userType}
          studentName={studentName}
          t={t}
          serviceName={serviceName}
          emptyStateTitle={emptyStateTitle}
          emptyStateSubTitle={emptyStateSubTitle}
          openLinkInNewTab={openLinkInNewTab}
        />
      ) : (
        <LTIIframe
          showModal={showModal}
          initialLaunchUrl={initialLaunchUrl}
          headerData={launchObject}
          onFrameLoaded={onFrameLoaded}
          isLTIMobileView={isLTIMobileView}
          serviceName={serviceName}
        />
      )}
    </div>
  );
});

LTIComponentWrapper.displayName = "LTIComponentWrapper";

const filterData = ({ userId, ...props }) => {
  return {
    launchInput: props,
    ids: [userId],
  };
};

const getFilteredDataMemoized = _.memoize(
  params => filterData(params),
  params => JSON.stringify(params)
);

const mapStateToProps = (state, ownProps) => {
  const {
    serviceName,
    courseId,
    resourceLinkId,
    resourceLinkTitle,
    filterDataExtraParams,
    resultActionType,
    curriculumId: curriculumIdFromProps,
    attachmentId,
    isLTIMobileView,
    isAdminLaunch = false,
    launchContext,
  } = ownProps;

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();
  const selectedLearningCourseId = _.get(
    state,
    "learningCourse.selectedLearningCourse.id",
    ""
  );

  const selectedLearningCourseIdForStudent = _.get(
    state,
    "teacher.selected_class.learningCourse.id",
    ""
  );

  const shouldUseSelectedLearningCourseId =
    isLearningCoursesEnabled && _.isEmpty(courseId);

  const shouldUseSelectedLearningCourseIdForStudent =
    isLearningCoursesEnabled &&
    ownProps.userType === "student" &&
    _.isEmpty(courseId);

  const userInfo = state.login.userInfo;
  const userType = _.get(userInfo, "user_type", "staff");
  const curriculumProgramId =
    curriculumIdFromProps || getSelectedCurriculumId(state);

  // Create the params object with the correct key for courseId
  const filterParams = {
    serviceName,
    userId: userInfo.id,
    resourceLinkId,
    resourceLinkTitle,
    resultActionType,
    curriculumProgramId,
    attachmentId,
    ...(launchContext ? { launchContext } : {}),
    ...filterDataExtraParams,
    ...(serviceName === "MS365" && isAdminLaunch && { isAdminLaunch: true }),
  };
  // Use the appropriate key based on the condition
  if (
    shouldUseSelectedLearningCourseId ||
    shouldUseSelectedLearningCourseIdForStudent
  ) {
    filterParams.learningCourseId =
      selectedLearningCourseId || selectedLearningCourseIdForStudent;
  } else {
    filterParams.courseId = courseId;
  }

  const filterData = getFilteredDataMemoized(filterParams);

  // for future reference, if you need to use the learningCourseId

  // const filterData = getFilteredDataMemoized({
  //   serviceName,
  //   userId: userInfo.id,
  //   courseId: updatedCourseId,
  //   resourceLinkId,
  //   resourceLinkTitle,
  //   resultActionType,
  //   curriculumProgramId,
  //   attachmentId,
  //   contextType: isLearningCoursesEnabled ? "learningCourse" : "course",
  //   ...filterDataExtraParams,
  // });

  return {
    isData: true,
    isLoading: false,
    filterData,
    serviceName,
    userType,
    isLTIMobileView,
  };
};

const mapActionCreators = {
  setToastMsg,
  openLinkInNewTab,
};

LTIComponentWrapper = compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getLtiLaunchData, {
    name: "getLtiLaunchDataQuery",
    options: ({ filterData }) => ({
      fetchPolicy: "network-only",
      variables: filterData,
    }),
    skip: ({ showEmptyState }) => showEmptyState,
    props: ({
      getLtiLaunchDataQuery,
      ownProps: {
        isData,
        isLoading,
        onContentItemSelection,
        serviceName,
        setToastMsg,
        onLTIModalSave,
        isStudent,
        toggleLTIComponent,
        handleError,
      },
    }) => {
      const { error } = getLtiLaunchDataQuery;

      if (error) {
        handleError();
      }

      const returnObject = _.first(
        _.get(getLtiLaunchDataQuery, `platform.user`, [])
      );

      const launchObject = _.get(
        returnObject,
        "ltiLaunchData.launchObject",
        {}
      );
      const launchUrl = _.get(returnObject, "ltiLaunchData.launchUrl", "");
      const isError = _.get(returnObject, "ltiLaunchData.errorFlag", true);
      const stopLinkCreation = _.get(
        returnObject,
        "ltiLaunchData.stopLinkCreation",
        true
      );
      const attachmentContextId = _.get(
        returnObject,
        "ltiLaunchData.attachmentContextId",
        null
      );
      const ltiMessageType = _.get(launchObject, "lti_message_type");

      const memoizedPusherEventHandler = _.memoize(
        params => {
          const { lti_errormsg, lti_errorlog } = params;
          if (ltiMessageType === "basic-lti-launch-request") {
            // Used for students
            if (isStudent) toggleLTIComponent();
            else if (lti_errormsg || lti_errorlog) {
              setToastMsg(lti_errormsg || lti_errorlog);
            }
            // Save attachment even if no lti_msg / lti_log
            else {
              onLTIModalSave({
                serviceName,
                attachmentContextId,
                initialLaunchUrl: launchUrl,
              });
            }
          } else if (onContentItemSelection)
            onContentItemSelection({ ...params, serviceName });
        },
        // Custom resolver function to create unique keys
        params => JSON.stringify(params)
      );

      return {
        launchObject,
        ltiMessageType,
        initialLaunchUrl: launchUrl,
        stopLinkCreation,
        attachmentContextId,
        isError,
        isData: !_.isEmpty(launchObject) && isData,
        isLoading:
          getLtiLaunchDataQuery["networkStatus"] == 1 ||
          getLtiLaunchDataQuery["networkStatus"] == 2 ||
          isLoading,
        onPusherEventTriggered: memoizedPusherEventHandler,
        clearMemoizedCache: () => {
          memoizedPusherEventHandler?.cache?.clear?.();
        },
      };
    },
  }),
  withPusherBind(["ltiSuccess"])
)(LTIComponentWrapper);

const LTIComponent = props => {
  const {
    showModal = true,
    toggleLTIComponent,
    serviceName,
    onLTIModalSave,
    showSaveButton = false,
    isLTIMobileView,
    showEmptyState = false,
  } = props;

  const useInAppBrowser = _.get(
    LTI_SERVICE_DETAILS[serviceName],
    "useInAppBrowser",
    false
  );
  const shouldUseInAppBrowser =
    serviceName === "CUSTOM" ? useInAppBrowser : false;

  const onClose = () => {
    if (isLTIMobileView || shouldUseInAppBrowser) {
      // post message to parent window to close the iframe
      if (window.ReactNativeWebView) {
        window?.ReactNativeWebView?.postMessage("CLOSE_LTI_WEBVIEW");
      }
    }

    toggleLTIComponent();
  };

  const { t } = useI18n();

  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const [ltiData, setLTIData] = useState({
    launchUrl: "",
    stopLinkCreation: true,
    attachmentContextId: "",
  });

  const onFrameLoaded = useCallback(() => {
    setIsIframeLoaded(true);
  }, [setIsIframeLoaded]);

  const onClickSave = () => {
    if (ltiData.stopLinkCreation) return;

    onLTIModalSave({
      serviceName,
      attachmentContextId: ltiData.attachmentContextId,
      initialLaunchUrl: ltiData.launchUrl,
    });
  };

  const handleError = useCallback(() => {
    setError(true);
  }, [setError]);

  const handleLoading = useCallback(
    load => {
      setLoading(load);
    },
    [setLoading]
  );

  const getLTIComponentWrapper = () => {
    return (
      <div className={classes.wrapperContainer}>
        <LTIComponentWrapper
          setLTIData={setLTIData}
          handleError={handleError}
          handleLoading={handleLoading}
          isLTIMobileView={isLTIMobileView}
          shouldUseInAppBrowser={shouldUseInAppBrowser}
          {...props}
          t={t}
          onFrameLoaded={onFrameLoaded}
        />
        {(!isIframeLoaded && !showEmptyState && !error) || loading ? (
          <div className={classes.loaderContainer}>
            <Loading containerStyle={{ height: "100%" }} />
          </div>
        ) : null}
      </div>
    );
  };

  const modalTitle = getLTIName(serviceName) || serviceName;

  return showModal ? (
    <UIModal
      isOpen
      onRequestClose={onClose}
      isFullScreen
      modalContent={classes.modalContent}
    >
      {!isLTIMobileView && !shouldUseInAppBrowser ? (
        <div className={classes.header}>
          <div className={classes.backButtonAndTitle}>
            <IconButton
              variant="plain"
              onClick={onClose}
              icon={<ChevronLeftOutlined />}
            />
            <div className={"heading-5"}>{modalTitle}</div>
          </div>
        </div>
      ) : null}
      {getLTIComponentWrapper()}
      {showSaveButton ? (
        <div className={classes.saveButton}>
          {!ltiData.stopLinkCreation ? (
            <Button variant={"progressive"} onClick={onClickSave}>
              {t("common:save")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </UIModal>
  ) : (
    getLTIComponentWrapper()
  );
};

export default LTIComponent;
